/**
 * Servicio OCR optimizado para boletas chilenas
 * Portado desde app-gestion-tiempo y adaptado al stack Kuadra2
 */
import Tesseract from 'tesseract.js';

/**
 * Comercios chilenos conocidos para mejor detección
 */
const CHILEAN_STORES = {
  supermarkets: [
    'LIDER', 'JUMBO', 'SANTA ISABEL', 'TOTTUS', 'UNIMARC', 'ACUENTA',
    'BIGGER', 'EKONO', 'MAYORISTA 10', 'CENTRAL MAYORISTA', 'ALVI',
    'OK MARKET', 'OXXO', 'BIG JOHN', 'MONTSERRAT', 'SUPER BODEGA'
  ],
  retail: [
    'FALABELLA', 'RIPLEY', 'PARIS', 'LA POLAR', 'HITES', 'ABC DIN',
    'CORONA', 'TRICOT', 'FASHION PARK', 'JOHNSON'
  ],
  pharmacies: [
    'CRUZ VERDE', 'AHUMADA', 'SALCOBRAND', 'DR SIMI', 'KNOP',
    'FARMACIA MAPUCHE', 'FARMACIAS DEL DR'
  ],
  gasStations: [
    'COPEC', 'SHELL', 'PETROBRAS', 'TERPEL', 'ENEX', 'ARAMCO'
  ],
  fastFood: [
    'MCDONALDS', 'MC DONALDS', 'BURGER KING', 'STARBUCKS', 'DUNKIN',
    'SUBWAY', 'KFC', 'PAPA JOHNS', 'PIZZA HUT', 'DOMINOS', 'TELEPIZZA',
    'DOGGIS', 'TARRAGONA', 'JUAN MAESTRO', 'LOMITOS'
  ],
  convenience: [
    'PRONTO', 'PUNTO', 'SPACIO 1', 'GLOBO', 'ON THE RUN'
  ],
  hardware: [
    'SODIMAC', 'EASY', 'CONSTRUMART', 'MTS', 'IMPERIAL', 'FERRETERIA'
  ],
  tech: [
    'PCFACTORY', 'PC FACTORY', 'WEPLASH', 'SOLOTODO', 'REIFSTORE',
    'MACSTORE', 'ISHOP', 'MOVISTAR', 'ENTEL', 'WOM', 'CLARO'
  ],
  restaurants: [
    'APPLEBEES', 'CHILIS', 'FRIDAYS', 'TGI', 'OUTBACK', 'OLIVE GARDEN',
    'BRAVISSIMO', 'HAPPENING', 'LIGURIA', 'GIRATORIO', 'ASTRID Y GASTON',
    'LA MAR', 'OSAKA', 'MATSURI', 'SUKIYA', 'BENIHANA',
    'LE BISTROT', 'BORDE RIO', 'PATIO BELLAVISTA'
  ]
};

/**
 * Mapeo de tipo de tienda → categoría Kuadra2
 */
const STORE_CATEGORIES = {
  supermarkets: 'food',
  retail: 'shopping',
  pharmacies: 'health',
  gasStations: 'transport',
  fastFood: 'food',
  convenience: 'food',
  hardware: 'housing',
  tech: 'services',
  restaurants: 'food'
};

/**
 * Mapeo categorías originales OCR → categorías Kuadra2
 */
const CATEGORY_MAP = {
  alimentacion: 'food',
  salud: 'health',
  transporte: 'transport',
  hogar: 'housing',
  ropa: 'shopping',
  entretenimiento: 'entertainment',
  educacion: 'education',
  servicios: 'services',
  belleza: 'shopping',
  otro: 'other'
};

/**
 * Palabras clave de productos para detectar categoría
 */
/**
 * Keywords que identifican un restaurant (propina-independiente)
 */
const RESTAURANT_KEYWORDS = [
  'PROPINA', 'MESERO', 'MOZO', 'GARZÓN', 'GARZON', 'MESA', 'CUBIERTO',
  'SERVICIO MESA', 'DESCORCHE', 'COPA', 'TRAGO', 'COCKTAIL', 'COCTEL',
  'ENTRADA', 'PLATO', 'PLATO FONDO', 'POSTRE', 'ACOMPAÑAMIENTO',
  'CARTA', 'MENU DEL DIA', 'EJECUTIVO', 'RESTAURANT', 'RESTAURANTE',
  'PARRILLA', 'CEVICHE', 'CARPACCIO', 'RISOTTO', 'LOMO', 'BIFE',
  'AGREGADO', 'GUARNICION', 'PORCION',
];

const PRODUCT_KEYWORDS = {
  alimentacion: [
    'LECHE', 'YOGURT', 'YOGHURT', 'QUESO', 'MANTEQUILLA', 'CREMA', 'QUESILLO',
    'POLLO', 'CARNE', 'CERDO', 'PAVO', 'JAMON', 'SALCHICHA', 'VIENESA', 'LONGANIZA',
    'CHORIZO', 'TOCINO', 'COSTILLA', 'FILETE', 'MOLIDA', 'CHULETA',
    'PAN', 'MARRAQUETA', 'HALLULLA', 'INTEGRAL', 'MOLDE',
    'MANZANA', 'PLATANO', 'BANANA', 'NARANJA', 'LIMON', 'TOMATE', 'LECHUGA',
    'PAPA', 'CEBOLLA', 'ZANAHORIA', 'PALTA', 'UVA', 'FRUTILLA', 'SANDIA',
    'ARROZ', 'FIDEO', 'PASTA', 'TALLARINES', 'SPAGUETTI', 'ACEITE', 'AZUCAR',
    'SAL', 'HARINA', 'ATUN', 'CONSERVA', 'SALSA', 'MAYONESA', 'KETCHUP', 'MOSTAZA',
    'COCA', 'PEPSI', 'FANTA', 'SPRITE', 'AGUA', 'MINERAL', 'JUGO', 'NECTAR',
    'CERVEZA', 'VINO', 'PISCO', 'BEBIDA', 'GASEOSA', 'ENERGETICA',
    'GALLETA', 'CHOCOLATE', 'CARAMELO', 'PAPA FRITA', 'CHIPS', 'RAMITAS', 'DORITOS',
    'CEREAL', 'BARRA', 'HELADO', 'MANI',
    'PIZZA', 'EMPANADA', 'SANDWICH', 'SUSHI', 'ENSALADA', 'ALMUERZO', 'MENU',
    'COMBO', 'HAMBURGUESA', 'COMPLETO', 'HOTDOG'
  ],
  salud: [
    'PARACETAMOL', 'IBUPROFENO', 'ASPIRINA', 'TAPSIN', 'KITADOL', 'ZALDIAR',
    'NASTIZOL', 'ANTIFLU', 'PROPOLEO', 'VITAMINA', 'OMEPRAZOL', 'LORATADINA',
    'CLONAZEPAM', 'ALPRAZOLAM', 'SERTRALINA', 'LOSARTAN', 'ATORVASTATINA',
    'METFORMINA', 'EUTIROX', 'LEVOTIROXINA', 'ANTICONCEPTIVO',
    'MEDICAMENTO', 'FARMACO', 'REMEDIO', 'ANTIBIOTICO', 'ANALGESICO',
    'ANTIINFLAMATORIO', 'ANTIHISTAMINICO', 'ANTIACIDO', 'LAXANTE',
    'TERMOMETRO', 'JERINGA', 'ALCOHOL', 'GASA', 'VENDA', 'PARCHE', 'CURITA',
    'MASCARILLA', 'GUANTE', 'SUERO', 'INHALADOR',
    'SUPLEMENTO', 'PROTEINA', 'CREATINA', 'COLAGENO', 'OMEGA', 'PROBIOTICO',
    'MULTIVITAMINICO', 'CALCIO', 'HIERRO', 'MAGNESIO', 'ZINC'
  ],
  transporte: [
    'BENCINA', 'GASOLINA', 'DIESEL', 'PETROLEO', 'GAS', 'COMBUSTIBLE',
    'OCTANO', '93', '95', '97',
    'LUBRICANTE', 'ACEITE MOTOR', 'FILTRO', 'NEUMATICO', 'LLANTA',
    'LAVADO', 'ESTACIONAMIENTO', 'PEAJE', 'TAG', 'AUTOPISTA',
    'PASAJE', 'BOLETO', 'METRO', 'MICRO', 'BUS', 'TRANSANTIAGO', 'RED', 'BIP',
    'UBER', 'DIDI', 'CABIFY', 'TAXI', 'TRANSFER'
  ],
  hogar: [
    'DETERGENTE', 'LAVALOZA', 'CLORO', 'DESINFECTANTE', 'LIMPIADOR',
    'ESCOBA', 'TRAPERO', 'ESPONJA', 'PAPEL HIGIENICO', 'SERVILLETA', 'TOALLA NOVA',
    'SUAVIZANTE', 'QUITAMANCHAS', 'AROMATIZANTE', 'AMBIENTADOR',
    'OLLA', 'SARTEN', 'PLATO', 'VASO', 'TAZA', 'CUBIERTO', 'CUCHILLO',
    'TORNILLO', 'CLAVO', 'PINTURA', 'BROCHA', 'MARTILLO', 'DESTORNILLADOR',
    'TALADRO', 'CABLE', 'ENCHUFE', 'AMPOLLETA', 'LED', 'FOCO',
    'MUEBLE', 'SILLA', 'MESA', 'ESTANTE', 'CORTINA', 'ALFOMBRA', 'COJIN'
  ],
  ropa: [
    'POLERA', 'CAMISA', 'PANTALON', 'JEANS', 'SHORT', 'FALDA', 'VESTIDO',
    'CHAQUETA', 'POLAR', 'PARKA', 'CHALECO', 'SWEATER', 'POLERON',
    'ROPA INTERIOR', 'CALZON', 'CALZONCILLO', 'SOSTEN', 'CALCETINES', 'MEDIAS',
    'ZAPATO', 'ZAPATILLA', 'SANDALIA', 'BOTA', 'TACO', 'ALPARGATA',
    'CINTURON', 'CARTERA', 'BOLSO', 'MOCHILA', 'BILLETERA', 'GORRO', 'BUFANDA',
    'GUANTES', 'RELOJ', 'LENTES', 'ANTEOJOS'
  ],
  entretenimiento: [
    'ENTRADA', 'CINE', 'PELICULA', 'FUNCION', 'TEATRO', 'CONCIERTO', 'SHOW',
    'ESPECTACULO', 'CIRCO', 'MUSEO', 'EXHIBICION',
    'VIDEOJUEGO', 'JUEGO', 'PLAYSTATION', 'XBOX', 'NINTENDO', 'STEAM',
    'SPOTIFY', 'NETFLIX', 'DISNEY', 'HBO', 'AMAZON PRIME', 'SUSCRIPCION',
    'GIMNASIO', 'GYM', 'PISCINA', 'CANCHA', 'BOWLING', 'KARAOKE',
    'ARRIENDO', 'BICICLETA', 'SKATE'
  ],
  educacion: [
    'CUADERNO', 'LAPIZ', 'LAPICERA', 'BOLIGRAFO', 'GOMA', 'CORRECTOR',
    'REGLA', 'COMPAS', 'TIJERA', 'PEGAMENTO', 'CARPETA', 'ARCHIVADOR',
    'DESTACADOR', 'PLUMON', 'MARCADOR',
    'LIBRO', 'TEXTO', 'DICCIONARIO', 'ENCICLOPEDIA', 'REVISTA', 'DIARIO',
    'MATRICULA', 'ARANCEL', 'MENSUALIDAD', 'CURSO', 'TALLER', 'CLASE',
    'CERTIFICACION', 'DIPLOMA', 'EXAMEN', 'FOTOCOPIAS', 'IMPRESION', 'ANILLADO'
  ],
  servicios: [
    'TELEFONO', 'CELULAR', 'PLAN', 'PREPAGO', 'RECARGA', 'GIGAS', 'INTERNET',
    'WIFI', 'FIBRA', 'CABLE', 'TELEVISION',
    'LUZ', 'ELECTRICIDAD', 'AGUA', 'GAS NATURAL', 'CALEFACCION',
    'COMPUTADOR', 'NOTEBOOK', 'LAPTOP', 'TABLET', 'IPAD', 'MOUSE', 'TECLADO',
    'MONITOR', 'PANTALLA', 'AUDIFONOS', 'PARLANTE', 'CARGADOR', 'CABLE USB',
    'PENDRIVE', 'DISCO DURO', 'MEMORIA', 'IMPRESORA', 'TINTA', 'TONER',
    'CONSULTA', 'ASESORIA', 'NOTARIA', 'TRAMITE', 'CERTIFICADO'
  ],
  belleza: [
    'SHAMPOO', 'ACONDICIONADOR', 'JABON', 'GEL', 'DESODORANTE', 'PERFUME',
    'COLONIA', 'CREMA', 'LOCION', 'BLOQUEADOR', 'PROTECTOR SOLAR',
    'MAQUILLAJE', 'BASE', 'POLVO', 'RUBOR', 'SOMBRA', 'RIMEL', 'MASCARA',
    'LABIAL', 'LIPSTICK', 'DELINEADOR', 'ESMALTE', 'UÑA',
    'CORTE', 'PEINADO', 'TINTURA', 'MECHAS', 'MANICURE', 'PEDICURE',
    'DEPILACION', 'TRATAMIENTO', 'FACIAL', 'MASAJE', 'SPA', 'PELUQUERIA'
  ]
};

/**
 * Preprocesa la imagen para mejorar el OCR
 */
export function preprocessImage(imageSource) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let width = img.width;
      let height = img.height;
      const maxWidth = 2000;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      // Adaptive threshold: compute local average to handle uneven lighting
      for (let i = 0; i < data.length; i += 4) {
        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        // Softer contrast to preserve number details
        const contrast = 1.3;
        const factor = (259 * (contrast * 100 + 255)) / (255 * (259 - contrast * 100));
        let newGray = factor * (gray - 128) + 128;
        newGray = Math.max(0, Math.min(255, newGray));
        data[i] = newGray;
        data[i + 1] = newGray;
        data[i + 2] = newGray;
      }

      ctx.putImageData(imageData, 0, 0);

      const sharpened = sharpenImage(ctx, width, height);
      ctx.putImageData(sharpened, 0, 0);

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => reject(new Error('Error loading image'));

    if (typeof imageSource === 'string') {
      img.src = imageSource;
    } else if (imageSource instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => { img.src = e.target.result; };
      reader.onerror = reject;
      reader.readAsDataURL(imageSource);
    } else if (imageSource instanceof HTMLImageElement) {
      img.src = imageSource.src;
    }
  });
}

function sharpenImage(ctx, width, height) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const copy = new Uint8ClampedArray(data);

  const kernel = [
    0, -1, 0,
    -1, 5, -1,
    0, -1, 0
  ];

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;
      let sum = 0;
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const kidx = ((y + ky) * width + (x + kx)) * 4;
          sum += copy[kidx] * kernel[(ky + 1) * 3 + (kx + 1)];
        }
      }
      data[idx] = data[idx + 1] = data[idx + 2] = Math.max(0, Math.min(255, sum));
    }
  }

  return imageData;
}

/**
 * Procesa una imagen de boleta con OCR
 */
export async function processReceipt(image, onProgress = () => {}) {
  onProgress({ status: 'optimizing', progress: 0.1 });
  const processedImage = await preprocessImage(image);

  onProgress({ status: 'recognizing', progress: 0.2 });

  // Run OCR twice: once for general text, once optimized for numbers
  const ocrConfig = {
    logger: m => {
      if (m.status === 'recognizing text') {
        onProgress({
          status: 'recognizing',
          progress: 0.2 + m.progress * 0.5,
          percent: Math.round(m.progress * 100)
        });
      }
    }
  };

  const result = await Tesseract.recognize(processedImage, 'spa', ocrConfig);

  // Second pass with original (non-preprocessed) image for better number detection
  onProgress({ status: 'recognizing', progress: 0.75, percent: 80 });
  const resultOriginal = await Tesseract.recognize(image, 'spa', {
    logger: () => {}
  });

  onProgress({ status: 'analyzing', progress: 0.85 });

  const text = result.data.text;
  const textOriginal = resultOriginal.data.text;

  // Parse both results
  const parsed = parseChileanReceipt(text);
  const parsedOriginal = parseChileanReceipt(textOriginal);

  // Merge: pick the BEST of each field from both passes
  const bestConfidence = Math.max(result.data.confidence, resultOriginal.data.confidence);
  const primaryIsProcessed = result.data.confidence >= resultOriginal.data.confidence;
  const primary = primaryIsProcessed ? parsed : parsedOriginal;
  const secondary = primaryIsProcessed ? parsedOriginal : parsed;

  const finalParsed = {
    // Store: prefer the longer/more detailed name (higher confidence text)
    store: primary.store || secondary.store || '',
    rut: primary.rut || secondary.rut,
    // Total: pick the highest valid total from either pass
    total: (() => {
      const t1 = parseInt(parsed.total) || 0;
      const t2 = parseInt(parsedOriginal.total) || 0;
      if (t1 > 0 && t2 > 0) return String(Math.max(t1, t2));
      return (t1 || t2) > 0 ? String(t1 || t2) : '';
    })(),
    // Date: prefer whichever found one
    date: primary.date || secondary.date,
    // Items: use the pass with more items detected
    items: parsed.items.length >= parsedOriginal.items.length ? parsed.items : parsedOriginal.items,
    description: primary.description || secondary.description,
    documentType: primary.documentType || secondary.documentType,
    hasTip: parsed.hasTip || parsedOriginal.hasTip,
    // Category: propina wins, then primary, then secondary
    suggestedCategory: (parsed.hasTip || parsedOriginal.hasTip)
      ? 'food'
      : (primary.suggestedCategory !== 'other' ? primary.suggestedCategory : secondary.suggestedCategory),
    productCategoryConfidence: Math.max(primary.productCategoryConfidence, secondary.productCategoryConfidence),
  };

  onProgress({ status: 'completed', progress: 1 });

  return {
    raw: text,
    rawOriginal: textOriginal,
    ...finalParsed,
    confidence: bestConfidence
  };
}

/**
 * Parsea el texto de una boleta chilena
 */
export function parseChileanReceipt(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const upperText = text.toUpperCase();

  let store = '';
  let rut = '';
  let total = '';
  let date = '';
  let items = [];
  let suggestedCategory = 'other';
  let documentType = 'boleta'; // boleta or factura

  // 0. Detectar tipo de documento (boleta vs factura)
  if (/FACTURA\s*(ELECTR[OÓ]NICA|AFECTA|EXENTA)/i.test(upperText) ||
      /FACTURA\s*DE\s*(VENTA|COMPRA)/i.test(upperText)) {
    documentType = 'factura';
  } else if (/BOLETA\s*(ELECTR[OÓ]NICA)?/i.test(upperText)) {
    documentType = 'boleta';
  } else if (/NOTA\s*DE\s*CR[EÉ]DITO/i.test(upperText)) {
    documentType = 'nota_credito';
  } else if (/NOTA\s*DE\s*D[EÉ]BITO/i.test(upperText)) {
    documentType = 'nota_debito';
  } else if (/GUIA\s*DE\s*DESPACHO/i.test(upperText)) {
    documentType = 'guia_despacho';
  }

  // 1. Buscar RUT chileno
  const rutPatterns = [
    /RUT[:\s]*(\d{1,2}\.?\d{3}\.?\d{3}-?[\dkK])/i,
    /R\.U\.T[:\s]*(\d{1,2}\.?\d{3}\.?\d{3}-?[\dkK])/i,
    /(\d{1,2}\.\d{3}\.\d{3}-[\dkK])/
  ];

  for (const pattern of rutPatterns) {
    const match = text.match(pattern);
    if (match) {
      rut = match[1].toUpperCase();
      break;
    }
  }

  // 2. Detectar tienda conocida
  for (const [category, stores] of Object.entries(CHILEAN_STORES)) {
    for (const storeName of stores) {
      if (upperText.includes(storeName)) {
        store = storeName;
        suggestedCategory = STORE_CATEGORIES[category] || 'other';
        break;
      }
    }
    if (store) break;
  }

  // 3. Buscar tienda por patrones en texto
  const STORE_EXCLUDE = /^(BOLETA|FACTURA|ELECTRONICA|RUT|FECHA|TOTAL|IVA|NETO|EXENTO|AFECTO|VUELTO|EFECTIVO|TARJETA|DEBITO|CREDITO|CAMBIO|PAGO|DETALLE|CANTIDAD|PRECIO|DESCUENTO|SUBTOTAL|DOCUMENTO|TRIBUTARIO|CONTRIBUYENTE|SII|TIMBRE|CEDIBLE|NO CEDIBLE)\b/i;

  if (!store) {
    const storePatterns = [
      // "Razón social: ..." or "Nombre fantasía: ..."
      /(?:RAZ[OÓ]N\s*SOCIAL|NOMBRE\s*(?:DE\s*)?FANTAS[IÍ]A|EMPRESA|COMERCIO)[:\s]*(.+)/i,
      // "Sucursal: ..."
      /SUCURSAL[:\s]*(.+)/i,
    ];

    for (const pattern of storePatterns) {
      const match = text.match(pattern);
      if (match) {
        const candidate = match[1].trim().replace(/\s+/g, ' ');
        if (!STORE_EXCLUDE.test(candidate) && candidate.length > 2 && candidate.length < 60) {
          store = candidate;
          break;
        }
      }
    }
  }

  // 4. Fallback: use first meaningful lines (Chilean receipts put store name at top)
  if (!store) {
    for (const line of lines.slice(0, 8)) {
      const clean = line.replace(/[^A-Za-záéíóúñÁÉÍÓÚÑ\s&'.,-]/g, '').trim();
      // Skip lines that are too short, too long, or are common receipt headers
      if (clean.length < 3 || clean.length > 50) continue;
      if (STORE_EXCLUDE.test(clean)) continue;
      // Skip lines that look like addresses (numbers at start)
      if (/^\d+\s/.test(line) && /\d{3,}/.test(line)) continue;
      // Skip lines that are just numbers
      if (/^[\d\s.,\-$]+$/.test(line)) continue;
      // Good candidate: has letters, not a receipt keyword
      if (/[A-Za-záéíóúñ]{2,}/.test(clean)) {
        store = clean;
        break;
      }
    }
  }

  // 5. Buscar TOTAL — pick highest between "total con propina" and "total a pagar"
  const textWithoutSubtotal = text.replace(/SUBTOTAL[^\n]*/gi, '');

  // Helper to clean OCR misreads in numbers
  const cleanOcrNumber = (str) => {
    return str
      .replace(/[Oo]/g, '0')
      .replace(/[lI]/g, '1')
      .replace(/\./g, '')
      .replace(/,/g, '');
  };

  // Extract a number from a regex match, returns 0 if invalid
  const extractAmount = (match) => {
    if (!match) return 0;
    const cleaned = cleanOcrNumber(match[1]);
    const num = parseInt(cleaned);
    return (num >= 50 && num <= 10000000) ? num : 0;
  };

  // Look for "total con propina" specifically — if found, it's a restaurant
  let hasTip = false;
  const tipPatterns = [
    /TOTAL\s*(?:CON|C\/)\s*PROPINA\s*[:\s]*\$?\s*([\d.,]+)/i,
    /TOTAL\s*\+\s*PROPINA\s*[:\s]*\$?\s*([\d.,]+)/i,
    /TOTAL\s*PROPINA\s*[:\s]*\$?\s*([\d.,]+)/i,
  ];
  let tipTotal = 0;
  for (const pattern of tipPatterns) {
    tipTotal = extractAmount(textWithoutSubtotal.match(pattern));
    if (tipTotal > 0) { hasTip = true; break; }
  }

  // Look for "total a pagar" and other standard totals
  const totalPatterns = [
    /TOTAL\s*A\s*PAGAR\s*[:\s]*\$?\s*([\d.,]+)/i,
    /TOTAL\s*\$?\s*([\d]+\.[\d]{3}(?:\.[\d]{3})*)/i,
    /(?<!SUB)TOTAL\s*[:\s]*\$?\s*([\d.,]+)/i,
    /MONTO\s*TOTAL\s*[:\s]*\$?\s*([\d.,]+)/i,
    /VALOR\s*TOTAL\s*[:\s]*\$?\s*([\d.,]+)/i,
    /IMPORTE\s*TOTAL\s*[:\s]*\$?\s*([\d.,]+)/i,
    /T\s*O\s*T\s*A\s*L\s*[:\s]*\$?\s*([\d.,]+)/i,
    /\$\s*([\d]{1,3}(?:[.,][\d]{3})+)\s*$/m,
    /(?<!SUB)TOTAL[:\s]+(\d{1,3}(?:[.,]\d{3})*)\s*$/im,
    /(?<!SUB)TOTAL\s*[:\s]*\$?\s*([\d.,OolI]+)/i,
  ];

  let standardTotal = 0;
  for (const pattern of totalPatterns) {
    standardTotal = extractAmount(textWithoutSubtotal.match(pattern));
    if (standardTotal > 0) break;
  }

  // Pick the highest amount — "total con propina" is always >= "total a pagar"
  const finalTotal = Math.max(tipTotal, standardTotal);
  total = finalTotal > 0 ? String(finalTotal) : '';

  // If propina detected, force category to food (restaurant)
  if (hasTip) {
    suggestedCategory = 'food';
  }

  // Also detect propina keyword anywhere (even if total con propina pattern didn't match)
  if (!hasTip && /PROPINA/i.test(upperText)) {
    hasTip = true;
    suggestedCategory = 'food';
  }

  // Detect restaurant by keywords (even without propina)
  if (suggestedCategory !== 'food') {
    const restaurantHits = RESTAURANT_KEYWORDS.filter(kw => upperText.includes(kw));
    if (restaurantHits.length >= 2) {
      suggestedCategory = 'food';
    }
  }

  // Fallback: find the largest number on lines near "TOTAL" keyword
  if (!total) {
    const totalLineIdx = lines.findIndex(l => /(?<!SUB)TOTAL/i.test(l));
    if (totalLineIdx !== -1) {
      const nearbyLines = lines.slice(Math.max(0, totalLineIdx - 1), totalLineIdx + 3);
      let maxAmount = 0;
      for (const line of nearbyLines) {
        const numbers = line.match(/\$?\s*([\d]{1,3}(?:[.,][\d]{3})*(?:[.,]\d{1,2})?)/g);
        if (numbers) {
          for (const num of numbers) {
            const cleaned = parseInt(cleanOcrNumber(num.replace(/\$/g, '')));
            if (cleaned > maxAmount && cleaned >= 50 && cleaned <= 10000000) {
              maxAmount = cleaned;
            }
          }
        }
      }
      if (maxAmount > 0) {
        total = String(maxAmount);
      }
    }
  }

  // 6. Buscar fecha
  const datePatterns = [
    /FECHA\s*(?:EMISION|EMI)?[:\s]*(\d{4})-(\d{2})-(\d{2})/i,
    /FECHA[:\s]*(\d{2})[\/\-](\d{2})[\/\-](\d{4})/i,
    /FECHA[:\s]*(\d{2})[\/\-](\d{2})[\/\-](\d{2})\b/i,
    /(\d{4})-(\d{2})-(\d{2})/,
    /(\d{2})[\/\-](\d{2})[\/\-](\d{4})/,
    /(\d{2})[\/\-](\d{2})[\/\-](\d{2})\b/,
    /(\d{1,2})\s+(?:de\s+)?(ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC)[A-Z]*\s+(?:de\s+)?(\d{2,4})/i,
    /(\d{1,2})\s+(ENERO|FEBRERO|MARZO|ABRIL|MAYO|JUNIO|JULIO|AGOSTO|SEPTIEMBRE|OCTUBRE|NOVIEMBRE|DICIEMBRE)\s+(\d{2,4})/i
  ];

  const monthMap = {
    'ene': '01', 'enero': '01', 'feb': '02', 'febrero': '02',
    'mar': '03', 'marzo': '03', 'abr': '04', 'abril': '04',
    'may': '05', 'mayo': '05', 'jun': '06', 'junio': '06',
    'jul': '07', 'julio': '07', 'ago': '08', 'agosto': '08',
    'sep': '09', 'septiembre': '09', 'oct': '10', 'octubre': '10',
    'nov': '11', 'noviembre': '11', 'dic': '12', 'diciembre': '12'
  };

  for (const pattern of datePatterns) {
    const match = text.match(pattern);
    if (match) {
      let day, month, year;

      if (match[1].length === 4) {
        year = match[1];
        month = match[2];
        day = match[3];
      } else if (isNaN(parseInt(match[2]))) {
        day = match[1].padStart(2, '0');
        month = monthMap[match[2].toLowerCase().substring(0, 3)] || monthMap[match[2].toLowerCase()];
        year = match[3].length === 2 ? '20' + match[3] : match[3];
      } else {
        day = match[1].padStart(2, '0');
        month = match[2].padStart(2, '0');
        year = match[3].length === 2 ? '20' + match[3] : match[3];
      }

      if (day && month && year &&
          parseInt(day) >= 1 && parseInt(day) <= 31 &&
          parseInt(month) >= 1 && parseInt(month) <= 12) {
        date = `${year}-${month}-${day}`;
        break;
      }
    }
  }

  // 7. Extraer items
  const itemPatterns = [
    /^\s*(\d+)\s+([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñ\s\d.]+?)\s+\$?([\d.,]+)\s*$/,
    /^\s*([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñ\s\d.]{3,30}?)\s+\$?([\d]{1,3}(?:\.[\d]{3})+)\s*$/,
    /^\s*[\d]+\s+([A-Za-záéíóúñ\s\d.]+?)\s+\$?([\d.,]+)\s*$/
  ];

  const excludeKeywords = [
    'total', 'subtotal', 'iva', 'neto', 'rut', 'boleta', 'factura',
    'fecha', 'hora', 'cantidad', 'precio', 'descuento', 'vuelto',
    'efectivo', 'tarjeta', 'debito', 'credito', 'cambio', 'pago'
  ];

  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    if (excludeKeywords.some(kw => lowerLine.includes(kw))) continue;

    for (const pattern of itemPatterns) {
      const match = line.match(pattern);
      if (match) {
        let itemName, itemPrice;
        if (match.length === 4) {
          itemName = match[2].trim();
          itemPrice = match[3];
        } else {
          itemName = match[1].trim();
          itemPrice = match[2];
        }

        if (itemName.length > 2 && itemName.length < 50) {
          const priceNum = parseInt(itemPrice.replace(/\./g, '').replace(/,/g, ''));
          if (priceNum >= 10 && priceNum <= 1000000) {
            items.push({
              name: itemName,
              price: priceNum,
              priceFormatted: `$${priceNum.toLocaleString('es-CL')}`
            });
          }
        }
        break;
      }
    }
  }

  items = items.slice(0, 20);

  // 8. Descripción automática
  let description = '';
  if (items.length > 0) {
    description = items.slice(0, 3).map(i => i.name).join(', ');
    if (items.length > 3) {
      description += ` (+${items.length - 3} más)`;
    }
  }

  // 9. Detectar categoría por productos
  const productCategory = detectCategoryByProducts(items, text);
  let finalCategory = suggestedCategory;

  // Propina always means restaurant/food — don't override
  if (hasTip) {
    finalCategory = 'food';
  } else if (productCategory.confidence >= 30) {
    finalCategory = CATEGORY_MAP[productCategory.category] || productCategory.category;
  } else if (suggestedCategory !== 'other') {
    finalCategory = suggestedCategory;
  }

  return {
    store: store || '',
    rut,
    total,
    date,
    items,
    description,
    documentType,
    hasTip,
    suggestedCategory: finalCategory,
    productCategoryConfidence: productCategory.confidence
  };
}

function detectCategoryByProducts(items, fullText) {
  const upperText = fullText.toUpperCase();
  const categoryScores = {};

  for (const category of Object.keys(PRODUCT_KEYWORDS)) {
    categoryScores[category] = 0;
  }

  for (const [category, keywords] of Object.entries(PRODUCT_KEYWORDS)) {
    for (const keyword of keywords) {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      const matches = upperText.match(regex);
      if (matches) {
        categoryScores[category] += matches.length;
      }
    }
  }

  for (const item of items) {
    const itemUpper = item.name.toUpperCase();
    for (const [category, keywords] of Object.entries(PRODUCT_KEYWORDS)) {
      for (const keyword of keywords) {
        if (itemUpper.includes(keyword)) {
          categoryScores[category] += 2;
        }
      }
    }
  }

  let maxScore = 0;
  let bestCategory = 'otro';

  for (const [category, score] of Object.entries(categoryScores)) {
    if (score > maxScore) {
      maxScore = score;
      bestCategory = category;
    }
  }

  return {
    category: bestCategory,
    confidence: Math.min(100, maxScore * 15)
  };
}
